import type { Grid, InventorySlot } from "@craftoff/game-core";
import { DECOY_ITEMS, MATCH_RECIPES } from "@craftoff/recipe-data";
import type { CoinRewardResult } from "./index";

export const MATCH_BINARY_SUBPROTOCOL = "craftoff.play.v6.binary";
export const BINARY_REJECT_SEQUENCE_GAP = 5;

const REQUEST_STATE = 0xfe;
const FORFEIT = 0xff;
const OPPONENT_OCCUPANCY = 0x81;
const COMMAND_REJECTED = 0x82;
const ROUND_STARTING = 0x83;
const ROUND_STARTED = 0x84;
const ROUND_ENDED = 0x85;
const MATCH_ENDED = 0x86;
const MATCH_ABORTED = 0x87;
const MATCH_STATE = 0x88;
const PROGRESSION_APPLIED = 0x89;
const SPECIAL_FREEZE_STARTED = 0x8a;
const SPECIAL_SCRAMBLE_STARTED = 0x8b;
const REMOVE_INVENTORY = 0x0f;
const NO_INDEX = 0xff;
const NO_NUMERIC_ID = 0xffff;

const recipeIds = MATCH_RECIPES.map((recipe) => recipe.id).sort();
const itemIds = [...DECOY_ITEMS].sort();
const phaseNames = ["waiting", "countdown", "active", "intermission", "complete", "aborted"] as const;
const reasonNames = [null, "completed", "timeout", "disconnect", "match_timeout", "connection_timeout", "aborted"] as const;

type BinaryMasterCraft = {
  playerIndex: number;
  round: number;
  recipeId: string;
  adjustedElapsedMs: number;
  thresholdMs: number;
};

export type BinaryMatchMessage =
  | { type: "OPPONENT_OCCUPANCY"; mask: number }
  | { type: "COMMAND_REJECTED"; sequence: number; reason: number }
  | { type: "ROUND_STARTING" | "ROUND_STARTED"; round: number; recipeId: string; at: number; inventory: InventorySlot[] }
  | { type: "ROUND_ENDED"; round: number; recipeId: string; winnerIndex: number | null; reason: string | null; score: [number, number]; intermissionEndsAt: number; masterCraft: BinaryMasterCraft | null }
  | { type: "MATCH_ENDED"; winnerIndex: number | null; reason: string | null; score: [number, number]; pendingProgression: boolean; masterCrafts: BinaryMasterCraft[] }
  | { type: "MATCH_ABORTED"; reason: string | null }
  | { type: "SPECIAL_FREEZE_STARTED"; actorIndex: number; freezeEndsAt: number; deadline: number }
  | { type: "SPECIAL_SCRAMBLE_STARTED"; actorIndex: number; order: number[] }
  | {
      type: "MATCH_STATE";
      state: {
        status: typeof phaseNames[number];
        youIndex: number;
        round: number;
        recipeId: string | null;
        completedRecipeId: string | null;
        score: [number, number];
        lastSequence: number;
        revision: number;
        roundStartsAt: number | null;
        deadline: number | null;
        intermissionEndsAt: number | null;
        intermissionWinnerIndex: number | null;
        intermissionReason: string | null;
        inventory: InventorySlot[];
        grids: [Grid, Grid];
      };
    }
  | {
      type: "PROGRESSION_APPLIED";
      xp: number;
      rankedRating: number;
      wins: number;
      losses: number;
      progressionVersion: number;
      coinBalance: number;
      coinReward: CoinRewardResult;
    };

function action(sequence: number, inventorySlot: number, gridSlot: number): Uint8Array<ArrayBuffer> {
  if (!Number.isInteger(sequence) || sequence <= 0 || sequence > 0xffff) {
    throw new RangeError("sequence must be a positive uint16");
  }
  if (!Number.isInteger(gridSlot) || gridSlot < 0 || gridSlot > 8) {
    throw new RangeError("grid slot must be between 0 and 8");
  }
  const bytes = new Uint8Array(3);
  new DataView(bytes.buffer).setUint16(0, sequence);
  bytes[2] = (inventorySlot << 4) | gridSlot;
  return bytes;
}

export function encodePlaceItem(sequence: number, inventorySlot: number, gridSlot: number): Uint8Array<ArrayBuffer> {
  if (!Number.isInteger(inventorySlot) || inventorySlot < 0 || inventorySlot > 9) {
    throw new RangeError("inventory slot must be between 0 and 9");
  }
  return action(sequence, inventorySlot, gridSlot);
}

export function encodeRemoveItem(sequence: number, gridSlot: number): Uint8Array<ArrayBuffer> {
  return action(sequence, REMOVE_INVENTORY, gridSlot);
}

export function encodeStateRequest(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(REQUEST_STATE);
}

export function encodeForfeit(): Uint8Array<ArrayBuffer> {
  return Uint8Array.of(FORFEIT);
}

class FrameReader {
  readonly bytes: Uint8Array;
  readonly view: DataView;
  offset = 0;

  constructor(data: ArrayBuffer) {
    this.bytes = new Uint8Array(data);
    this.view = new DataView(data);
  }

  remaining(): number { return this.bytes.length - this.offset; }
  u8(): number { if (this.remaining() < 1) throw new RangeError(); return this.bytes[this.offset++]!; }
  u16(): number { if (this.remaining() < 2) throw new RangeError(); const value = this.view.getUint16(this.offset); this.offset += 2; return value; }
  u32(): number { if (this.remaining() < 4) throw new RangeError(); const value = this.view.getUint32(this.offset); this.offset += 4; return value; }
  i64(): number { if (this.remaining() < 8) throw new RangeError(); const value = Number(this.view.getBigUint64(this.offset)); this.offset += 8; return value; }
}

function recipeId(value: number): string | null {
  return value === NO_NUMERIC_ID ? null : recipeIds[value] ?? null;
}

function reason(value: number): string | null {
  return reasonNames[value] ?? null;
}

function optionalIndex(value: number): number | null {
  return value === NO_INDEX ? null : value <= 1 ? value : null;
}

function inventory(reader: FrameReader): InventorySlot[] {
  const count = reader.u8();
  const result: InventorySlot[] = [];
  for (let index = 0; index < count; index += 1) {
    const itemId = itemIds[reader.u16()];
    const quantity = reader.u8();
    if (!itemId || quantity < 1) throw new RangeError();
    result.push({ slotId: `inventory-${index}`, itemId, quantity });
  }
  return result;
}

function masterCraft(reader: FrameReader, firstIndex?: number): BinaryMasterCraft | null {
  const playerIndex = firstIndex ?? reader.u8();
  if (playerIndex === NO_INDEX) return null;
  const round = reader.u8();
  const resolvedRecipe = recipeId(reader.u16());
  const adjustedElapsedMs = reader.u32();
  const thresholdMs = reader.u32();
  if (playerIndex > 1 || !resolvedRecipe) throw new RangeError();
  return { playerIndex, round, recipeId: resolvedRecipe, adjustedElapsedMs, thresholdMs };
}

function setup(reader: FrameReader, type: "ROUND_STARTING" | "ROUND_STARTED"): BinaryMatchMessage {
  const round = reader.u8();
  const resolvedRecipe = recipeId(reader.u16());
  const at = reader.i64();
  const slots = inventory(reader);
  if (!resolvedRecipe || reader.remaining() !== 0) throw new RangeError();
  return { type, round, recipeId: resolvedRecipe, at, inventory: slots };
}

export function decodeBinaryMatchMessage(data: ArrayBuffer): BinaryMatchMessage | null {
  try {
    const reader = new FrameReader(data);
    const opcode = reader.u8();
    if (opcode === OPPONENT_OCCUPANCY) {
      const mask = reader.u16();
      return mask <= 0x1ff && reader.remaining() === 0 ? { type: "OPPONENT_OCCUPANCY", mask } : null;
    }
    if (opcode === COMMAND_REJECTED) {
      const result = { type: "COMMAND_REJECTED" as const, sequence: reader.u16(), reason: reader.u8() };
      return reader.remaining() === 0 ? result : null;
    }
    if (opcode === ROUND_STARTING) return setup(reader, "ROUND_STARTING");
    if (opcode === ROUND_STARTED) return setup(reader, "ROUND_STARTED");
    if (opcode === ROUND_ENDED) {
      const round = reader.u8();
      const resolvedRecipe = recipeId(reader.u16());
      const winnerIndex = optionalIndex(reader.u8());
      const roundReason = reason(reader.u8());
      const score: [number, number] = [reader.u8(), reader.u8()];
      const intermissionEndsAt = reader.i64();
      const craft = masterCraft(reader);
      if (!resolvedRecipe || reader.remaining() !== 0) return null;
      return { type: "ROUND_ENDED", round, recipeId: resolvedRecipe, winnerIndex, reason: roundReason, score, intermissionEndsAt, masterCraft: craft };
    }
    if (opcode === MATCH_ENDED) {
      const winnerIndex = optionalIndex(reader.u8());
      const matchReason = reason(reader.u8());
      const score: [number, number] = [reader.u8(), reader.u8()];
      const pendingProgression = reader.u8() === 1;
      const count = reader.u8();
      const masterCrafts = Array.from({ length: count }, () => masterCraft(reader)).filter((value): value is BinaryMasterCraft => value !== null);
      return reader.remaining() === 0 ? { type: "MATCH_ENDED", winnerIndex, reason: matchReason, score, pendingProgression, masterCrafts } : null;
    }
    if (opcode === MATCH_ABORTED) {
      const result = { type: "MATCH_ABORTED" as const, reason: reason(reader.u8()) };
      return reader.remaining() === 0 ? result : null;
    }
    if (opcode === SPECIAL_FREEZE_STARTED) {
      const actorIndex = reader.u8();
      const freezeEndsAt = reader.i64();
      const deadline = reader.i64();
      return actorIndex <= 1 && reader.remaining() === 0
        ? { type: "SPECIAL_FREEZE_STARTED", actorIndex, freezeEndsAt, deadline }
        : null;
    }
    if (opcode === SPECIAL_SCRAMBLE_STARTED) {
      const actorIndex = reader.u8();
      const count = reader.u8();
      const order = Array.from({ length: count }, () => reader.u8());
      const validOrder = count > 1
        && count <= 10
        && new Set(order).size === count
        && order.every((value) => value < count);
      return actorIndex <= 1 && validOrder && reader.remaining() === 0
        ? { type: "SPECIAL_SCRAMBLE_STARTED", actorIndex, order }
        : null;
    }
    if (opcode === MATCH_STATE) {
      const phase = phaseNames[reader.u8()];
      const youIndex = reader.u8();
      const round = reader.u8();
      const activeRecipe = recipeId(reader.u16());
      const completedRecipe = recipeId(reader.u16());
      const score: [number, number] = [reader.u8(), reader.u8()];
      const lastSequence = reader.u16();
      const revision = reader.u32();
      const roundStartsAt = reader.i64() || null;
      const deadline = reader.i64() || null;
      const intermissionEndsAt = reader.i64() || null;
      const intermissionWinnerIndex = optionalIndex(reader.u8());
      const intermissionReason = reason(reader.u8());
      const slots = inventory(reader);
      const grids = [0, 1].map(() => Array.from({ length: 9 }, () => {
        const value = reader.u8();
        return value === 0 ? null : slots[value - 1]?.itemId ?? null;
      }) as Grid) as [Grid, Grid];
      if (!phase || youIndex > 1 || reader.remaining() !== 0) return null;
      return {
        type: "MATCH_STATE",
        state: {
          status: phase, youIndex, round, recipeId: activeRecipe, completedRecipeId: completedRecipe,
          score, lastSequence, revision, roundStartsAt, deadline, intermissionEndsAt,
          intermissionWinnerIndex, intermissionReason, inventory: slots, grids
        }
      };
    }
    if (opcode === PROGRESSION_APPLIED) {
      const xp = reader.u32();
      const rankedRating = reader.u32();
      const wins = reader.u32();
      const losses = reader.u32();
      const progressionVersion = reader.u32();
      const coinBalance = reader.u32();
      const values = Array.from({ length: 7 }, () => reader.u32());
      const milestoneCount = reader.u8();
      const xpMilestones = Array.from({ length: milestoneCount }, () => reader.u32());
      if (reader.remaining() !== 0) return null;
      const coinReward: CoinRewardResult = {
        coinBalance,
        coinsGranted: values[0]!,
        xpMilestoneCoins: values[1]!,
        masterCraftCoins: values[2]!,
        masterCraftsRecognized: values[3]!,
        masterCraftsRewarded: values[4]!,
        masterCraftsToday: values[5]!,
        masterCraftDailyLimit: values[6]!,
        xpMilestones
      };
      return { type: "PROGRESSION_APPLIED", xp, rankedRating, wins, losses, progressionVersion, coinBalance, coinReward };
    }
    return null;
  } catch {
    return null;
  }
}
